/* CUSTOM_BULKARM_BY_CLAUDE (AIOPS `965dbb7041e7`, Ryan 2026-09-30) - AI Agents -> Agents: paste one list, arm many.
   ─────────────────────────────────────────────────────────────────────────────
   Ryan: paste one list to arm many capabilities at once (a new agent joining, a role change) instead of
   clicking one drop-down at a time. Elon reads the whole picture (GET /agent-access-matrix, `16fbf86604d5`) and
   hands him a line like `ONYX: followups.retier on, eta.notice.send window`.

   FORMAT: comma- (or line-) separated `capability mode` entries. A line may open with `SEAT:` (code or name);
   otherwise the seat picker decides. Modes: on | off | direct | window | approval.
   ⛔ "on" IS THE GENTLEST ARMED MODE THE CAPABILITY OFFERS: window where it can be held, else direct. So a send
      capability pasted "on" is held, never sent straight out, and the preview shows which word it became.
   ⛔ "window 7d" IS REFUSED, NOT GUESSED: window is the 15-minute undo hold, not a time-limited grant (Ryan's
      call 2026-09-30, the session's question 2). Anything after the mode word is an error on that line.
   ⛔ NOTHING CHANGES UNTIL APPLY, AND APPLY IS OFF WHILE ANY LINE IS RED. A half-understood list is the one
      thing this box must not save.
   ⛔ ONLY THE LISTED CAPABILITIES MOVE. /agent-capabilities REPLACES the whole set, so each seat is posted the
      page's own full map (every catalog id, the card's own save shape) with just the listed ones changed. The
      worker validates every mode against the catalog again and audits who changed what.
   Pure parts (`parseBulkArm`, `planBulkArm`) are exported for tests/agent-bulk-arm-tests.mjs; `mount` is the
   page half and runs only when the section hands it a bridge. */
(function (root) {
  'use strict';
  var MODES = ['on', 'off', 'direct', 'window', 'approval'];
  var SEAT_RX = /^\s*([A-Za-z][A-Za-z0-9 ]{1,30}?)\s*:\s*/;

  function escHtml(v) {
    return String(v == null ? '' : v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function findSeat(agents, word) {
    var w = String(word || '').trim().toLowerCase();
    if (!w) return null;
    for (var i = 0; i < agents.length; i++) if (String(agents[i].code).toLowerCase() === w) return agents[i];
    for (var j = 0; j < agents.length; j++) if (String(agents[j].name || '').toLowerCase() === w) return agents[j];
    return null;
  }

  /** "on" -> window where the capability can be held, else direct. Any other word is itself. */
  function resolveMode(word, gates) {
    if (word !== 'on') return word;
    return gates.indexOf('window') !== -1 ? 'window' : 'direct';
  }

  /**
   * Text -> one row per entry. Pure.
   * @returns {{rows: Array, errors: number}} row: {seat, cap, label, from, to, status: change|same|error|replaced, note, raw}
   */
  function parseBulkArm(text, opts) {
    var catalog = (opts && opts.catalog) || [];
    var agents = (opts && opts.agents) || [];
    var pickerSeat = opts && opts.seat;
    var byId = {};
    catalog.forEach(function (c) { byId[c.id] = c; });
    var rows = [];
    String(text || '').split(/\r?\n/).forEach(function (line) {
      var seatWord = null;
      var m = line.match(SEAT_RX);
      if (m && !byId[m[1].trim()]) { seatWord = m[1]; line = line.slice(m[0].length); }
      line.split(/[,;]/).forEach(function (entry) {
        var raw = entry.trim();
        if (!raw) return;
        var row = { raw: raw, seat: null, cap: null, label: '', from: null, to: null, status: 'error', note: '' };
        rows.push(row);
        var seat = findSeat(agents, seatWord || pickerSeat);
        if (!seat) { row.note = seatWord ? 'No agent called "' + seatWord + '".' : 'Pick a seat, or start the line with one (ONYX: ...).'; return; }
        row.seat = seat.code;
        if (seat.status !== 'active') { row.note = seat.code + ' is ' + seat.status + '; a revoked agent cannot be armed.'; return; }
        var tokens = raw.split(/\s+/);
        var cap = byId[tokens[0]];
        row.cap = tokens[0];
        if (!cap) { row.note = 'Unknown capability "' + tokens[0] + '".'; return; }
        row.label = cap.label || cap.id;
        var gates = cap.gates || [];
        row.from = (seat.capabilities && seat.capabilities[cap.id]) || 'off';
        var word = String(tokens[1] || '').toLowerCase();
        if (!word) { row.note = 'No mode. Add one of: ' + MODES.join(', ') + '.'; return; }
        if (MODES.indexOf(word) === -1) { row.note = 'Unknown mode "' + tokens[1] + '". Use one of: ' + MODES.join(', ') + '.'; return; }
        if (tokens.length > 2) {
          row.note = word === 'window'
            ? '"' + tokens.slice(2).join(' ') + '" is not supported: window is the 15-minute undo hold on a send, not a grant that expires.'
            : 'Unexpected "' + tokens.slice(2).join(' ') + '" after the mode.';
          return;
        }
        var to = resolveMode(word, gates);
        if (gates.indexOf(to) === -1) { row.note = cap.id + ' cannot be set to ' + to + '. It takes: ' + gates.join(', ') + '.'; return; }
        row.to = to;
        if (word === 'on') row.note = '"on" means ' + to + ' here.';
        row.status = to === row.from ? 'same' : 'change';
      });
    });
    /* A later line about the same seat + capability wins; the earlier one says so. */
    var seen = {};
    for (var i = rows.length - 1; i >= 0; i--) {
      var r = rows[i];
      if (r.status !== 'change' && r.status !== 'same') continue;
      var k = r.seat + '|' + r.cap;
      if (seen[k]) { r.status = 'replaced'; r.note = 'Replaced by a later line for the same capability.'; }
      else seen[k] = true;
    }
    return { rows: rows, errors: rows.filter(function (r) { return r.status === 'error'; }).length };
  }

  /** Rows -> one full map per seat that changes (every catalog id, the card's own save shape). Pure. */
  function planBulkArm(rows, opts) {
    var catalog = (opts && opts.catalog) || [];
    var agents = (opts && opts.agents) || [];
    var bySeat = {};
    rows.forEach(function (r) {
      if (r.status !== 'change') return;
      (bySeat[r.seat] = bySeat[r.seat] || []).push(r);
    });
    return Object.keys(bySeat).map(function (code) {
      var seat = findSeat(agents, code);
      var caps = {};
      catalog.forEach(function (c) { caps[c.id] = (seat && seat.capabilities && seat.capabilities[c.id]) || 'off'; });
      bySeat[code].forEach(function (r) { caps[r.cap] = r.to; });
      return { code: code, capabilities: caps, changes: bySeat[code].map(function (r) { return { cap: r.cap, from: r.from, to: r.to }; }) };
    });
  }

  /* CUSTOM_PERMBUNDLE_BY_CLAUDE (AIOPS 53a6489bae6f, Ryan 2026-10-30) - a lane bundle becomes ordinary lines for this box.
     The worker (GET /agent-access/bundles?seat=) says what the bundle would turn on for the seat and what it holds back
     (money, outside email, customer records, logins: Ryan ticks each). Ticked rows -> `SEAT: id mode, ...`, at the lane
     lead's own mode. ⛔ The box's own Preview + Apply is still the only save, and a bundle never lists anything to turn off.
     Pure: `ids` = the ticked capability ids. */
  function bundleText(seat, bundle, ids) {
    var want = {};
    (ids || []).forEach(function (id) { want[id] = true; });
    var rows = [].concat((bundle && bundle.turnOn) || [], (bundle && bundle.sensitive) || []).filter(function (i) { return want[i.id]; });
    return rows.length ? String(seat).toUpperCase() + ': ' + rows.map(function (i) { return i.id + ' ' + i.mode; }).join(', ') : '';
  }

  /* ── the page half ──────────────────────────────────────────────────────── */
  var B = null;
  var LAST = null;
  /* CUSTOM_PERMBUNDLE_BY_CLAUDE - the lane keys are the worker's (lib/agent-bundles.js LANES); the worker says what is in each. */
  var LANES = [['order-ops', 'Order Ops'], ['logistics', 'Logistics'], ['customer-care', 'Customer Care'], ['tax', 'Tax'],
    ['product', 'Product'], ['engineer', 'Engineer'], ['chief-of-staff', 'Chief of Staff']];
  var BUNDLE = null;

  function panelHtml() {
    var esc = escHtml;
    var agents = B.agents().filter(function (a) { return a.status === 'active'; });
    var tab = B.tab();
    var seats = agents.map(function (a) {
      return '<option value="' + esc(a.code) + '"' + (a.code === tab ? ' selected' : '') + '>' + esc(a.name) + ' (' + esc(a.code) + ')</option>';
    }).join('');
    return '<details class="rqa-bulk" style="margin:0 0 18px;border:2px solid var(--rq-border,#1e2a42);border-radius:12px;padding:12px 16px;">'
      + '<summary style="cursor:pointer;font-weight:700;">Paste a list to arm many at once</summary>'
      + '<p style="font-size:12.5px;color:var(--rq-text-muted,#8892a0);margin:10px 0;">One entry per capability, separated by commas or new lines: '
      + '<code>capability mode</code>. Modes: on, off, direct, window, approval. "on" arms it at its gentlest level (held where it can be held). '
      + 'Start a line with a seat (<code>ONYX:</code>) to name who, or pick one here. Nothing changes until you press Apply.</p>'
      + '<div style="display:flex;gap:8px;align-items:center;margin:0 0 8px;flex-wrap:wrap;">'
      + '<label for="rqa-bulk-seat" style="font-size:13px;">Seat</label>'
      + '<select class="rqa-select" id="rqa-bulk-seat">' + seats + '</select></div>'
      + '<div style="display:flex;gap:8px;align-items:center;margin:0 0 8px;flex-wrap:wrap;">'   // CUSTOM_PERMBUNDLE_BY_CLAUDE
      + '<label for="rqa-bulk-lane" style="font-size:13px;">Start from a lane bundle</label>'
      + '<select class="rqa-select" id="rqa-bulk-lane"><option value="">Choose a lane</option>' + LANES.map(function (l) { return '<option value="' + esc(l[0]) + '">' + esc(l[1]) + '</option>'; }).join('') + '</select>'
      + '<button class="rqa-btn" id="rqa-bulk-lane-load" type="button">Show bundle</button></div>'
      + '<div id="rqa-bulk-bundle"></div>'
      + '<textarea class="rqa-textarea" id="rqa-bulk-text" style="width:100%;" placeholder="ONYX: followups.retier on, eta.notice.send window, abby.reply.abort off"></textarea>'
      + '<div class="rqa-actions">'
      + '<button class="rqa-btn" id="rqa-bulk-preview" type="button">Preview</button>'
      + '<button class="rqa-btn rqa-btn--send" id="rqa-bulk-apply" type="button" disabled>Apply</button></div>'
      + '<div id="rqa-bulk-out"></div></details>';
  }

  function previewHtml(parsed) {
    var esc = escHtml;
    if (!parsed.rows.length) return '<div class="rqa-note rqa-note--bad">Nothing to read. Paste at least one "capability mode" entry.</div>';
    var changes = parsed.rows.filter(function (r) { return r.status === 'change'; }).length;
    var head = parsed.errors
      ? '<div class="rqa-note rqa-note--bad">' + parsed.errors + ' line' + (parsed.errors === 1 ? '' : 's') + ' in red. Fix them and press Preview again; Apply stays off until every line reads.</div>'
      : '<div class="rqa-note rqa-note--ok">' + changes + ' change' + (changes === 1 ? '' : 's') + ' ready. Press Apply to save them.</div>';
    var body = parsed.rows.map(function (r) {
      var bad = r.status === 'error';
      var colour = bad ? '#dc5046' : (r.status === 'change' ? 'var(--rq-text,#e0e0e0)' : 'var(--rq-text-muted,#8892a0)');
      var what = bad ? 'error' : (r.status === 'change' ? r.from + ' &rarr; <b>' + esc(r.to) + '</b>' : (r.status === 'same' ? 'already ' + esc(r.to) : 'skipped'));
      return '<tr style="color:' + colour + ';">'
        + '<td style="padding:4px 8px;">' + esc(r.seat || '?') + '</td>'
        + '<td style="padding:4px 8px;"><code>' + esc(r.cap || r.raw) + '</code>' + (r.label ? '<br><small>' + esc(r.label) + '</small>' : '') + '</td>'
        + '<td style="padding:4px 8px;white-space:nowrap;">' + what + '</td>'
        + '<td style="padding:4px 8px;font-size:12px;">' + esc(r.note) + '</td></tr>';
    }).join('');
    return head + '<div style="overflow-x:auto;"><table style="border-collapse:collapse;font-size:13px;margin-top:8px;">'
      + '<thead><tr style="text-align:left;"><th style="padding:4px 8px;">Seat</th><th style="padding:4px 8px;">Capability</th><th style="padding:4px 8px;">Change</th><th style="padding:4px 8px;"></th></tr></thead>'
      + '<tbody>' + body + '</tbody></table></div>';
  }

  function read() {
    return parseBulkArm(document.getElementById('rqa-bulk-text').value, {
      catalog: B.catalog(), agents: B.agents(), seat: document.getElementById('rqa-bulk-seat').value,
    });
  }

  function doPreview() {
    LAST = read();
    document.getElementById('rqa-bulk-out').innerHTML = previewHtml(LAST);
    var changes = LAST.rows.some(function (r) { return r.status === 'change'; });
    document.getElementById('rqa-bulk-apply').disabled = !!LAST.errors || !changes;
  }

  async function doApply() {
    var btn = document.getElementById('rqa-bulk-apply');
    btn.disabled = true;
    /* Re-read against the page's CURRENT copy: a drop-down moved since Preview must not be overwritten. */
    var parsed = read();
    if (parsed.errors) { LAST = parsed; document.getElementById('rqa-bulk-out').innerHTML = previewHtml(parsed); return; }
    var plan = planBulkArm(parsed.rows, { catalog: B.catalog(), agents: B.agents() });
    var lines = [];
    for (var i = 0; i < plan.length; i++) {
      var p = plan[i];
      var r = await B.api('/agent-capabilities', { method: 'POST', body: JSON.stringify({ code: p.code, capabilities: p.capabilities }) });
      if (r.ok) {
        B.saved(p.code, (r.body && r.body.agent && r.body.agent.capabilities) || p.capabilities);
        lines.push('<div class="rqa-note rqa-note--ok">' + escHtml(p.code) + ': ' + p.changes.map(function (c) { return escHtml(c.cap) + ' ' + escHtml(c.from) + ' &rarr; ' + escHtml(c.to); }).join(', ') + '</div>');
      } else {
        lines.push('<div class="rqa-note rqa-note--bad">' + escHtml(p.code) + ': not saved (' + escHtml((r.body && r.body.error) || r.status) + '). Nothing changed for this seat.</div>');
      }
    }
    document.getElementById('rqa-bulk-out').innerHTML = (lines.join('') || '<div class="rqa-note rqa-note--ok">Nothing to change.</div>')
      + '<p style="font-size:12px;color:var(--rq-text-muted,#8892a0);">Saved changes are on each agent\'s tab below and in the audit log under your name.</p>';
  }

  /* CUSTOM_PERMBUNDLE_BY_CLAUDE - Show bundle: the worker's diff for the picked seat, plain rows ticked, held-back rows not. */
  function bundleHtml(b) {
    var esc = escHtml;
    var row = function (i, on) {
      return '<label style="display:block;font-size:13px;margin:2px 0;"><input type="checkbox" class="rqa-bulk-pick" value="' + esc(i.id) + '"' + (on ? ' checked' : '') + '> '
        + '<code>' + esc(i.id) + '</code> ' + esc(i.mode) + ' <small style="color:var(--rq-text-muted,#8892a0);">' + esc(i.label) + (i.sensitiveWhy ? ' (held back: ' + esc(i.sensitiveWhy) + ')' : '') + '</small></label>';
    };
    var head = '<div class="rqa-note">' + esc(b.label) + (b.leadLive ? ' copies ' + esc(b.lead) + '\u2019s permissions plus the four basics' : ': ' + esc(b.lead) + ' is not a live seat, so this is the four basics only')
      + '. For ' + esc(b.seat) + ': ' + b.turnOn.length + ' to turn on, ' + b.sensitive.length + ' held back for you to tick, ' + b.alreadyHeld + ' already held.</div>';
    if (!b.turnOn.length && !b.sensitive.length) return head + '<div class="rqa-note rqa-note--ok">Nothing to add: ' + esc(b.seat) + ' already holds this whole bundle.</div>';
    return head
      + (b.turnOn.length ? '<div style="margin:8px 0 4px;font-weight:700;font-size:13px;">Turn on</div>' + b.turnOn.map(function (i) { return row(i, true); }).join('') : '')
      + (b.sensitive.length ? '<div style="margin:8px 0 4px;font-weight:700;font-size:13px;">Held back (money, outside email, customer records, logins): tick each one you want</div>' + b.sensitive.map(function (i) { return row(i, false); }).join('') : '')
      + (b.outside.length ? '<p style="font-size:12px;color:var(--rq-text-muted,#8892a0);">Already on for ' + esc(b.seat) + ' but not in this bundle (left exactly as they are): ' + b.outside.map(esc).join(', ') + '</p>' : '')
      + '<div class="rqa-actions"><button class="rqa-btn" id="rqa-bulk-lane-use" type="button">Put the ticked ones in the box</button></div>';
  }

  async function doBundle() {
    var out = document.getElementById('rqa-bulk-bundle');
    var lane = document.getElementById('rqa-bulk-lane').value;
    var seat = document.getElementById('rqa-bulk-seat').value;
    BUNDLE = null;
    if (!lane) { out.innerHTML = '<div class="rqa-note rqa-note--bad">Choose a lane first.</div>'; return; }
    out.innerHTML = '<div class="rqa-note">Loading&hellip;</div>';
    var r = await B.api('/agent-access/bundles?seat=' + encodeURIComponent(seat));
    var b = r.ok && r.body && (r.body.lanes || []).filter(function (l) { return l.key === lane; })[0];
    if (!b) { out.innerHTML = '<div class="rqa-note rqa-note--bad">Could not load the bundle (' + escHtml((r.body && r.body.error) || r.status) + '). Nothing changed.</div>'; return; }
    BUNDLE = b;
    out.innerHTML = bundleHtml(b);
    var use = document.getElementById('rqa-bulk-lane-use');
    if (use) use.addEventListener('click', function () {
      var ids = [].slice.call(out.querySelectorAll('.rqa-bulk-pick')).filter(function (c) { return c.checked; }).map(function (c) { return c.value; });
      var text = bundleText(BUNDLE.seat, BUNDLE, ids);
      if (!text) { out.insertAdjacentHTML('beforeend', '<div class="rqa-note rqa-note--bad">Nothing ticked.</div>'); return; }
      document.getElementById('rqa-bulk-text').value = text;
      doPreview();
    });
  }

  /** Called by the section once the agent list has loaded (and again on each reload). */
  function mount(bridge) {
    B = bridge || root.RqaAgentsBridge || B;
    if (!B || !B.host) return;
    var open = B.host.querySelector('details') && B.host.querySelector('details').open;
    var keep = document.getElementById('rqa-bulk-text') ? document.getElementById('rqa-bulk-text').value : '';
    B.host.innerHTML = panelHtml();
    if (open) B.host.querySelector('details').open = true;
    document.getElementById('rqa-bulk-text').value = keep;
    document.getElementById('rqa-bulk-preview').addEventListener('click', doPreview);
    document.getElementById('rqa-bulk-lane-load').addEventListener('click', doBundle);   // CUSTOM_PERMBUNDLE_BY_CLAUDE
    document.getElementById('rqa-bulk-apply').addEventListener('click', doApply);
    /* Any edit after a Preview turns Apply off again: what is applied must be what was previewed. */
    document.getElementById('rqa-bulk-text').addEventListener('input', function () { document.getElementById('rqa-bulk-apply').disabled = true; });
    document.getElementById('rqa-bulk-seat').addEventListener('change', function () { document.getElementById('rqa-bulk-apply').disabled = true; BUNDLE = null; document.getElementById('rqa-bulk-bundle').innerHTML = ''; });
  }

  root.RqaBulkArm = { parseBulkArm: parseBulkArm, planBulkArm: planBulkArm, resolveMode: resolveMode, bundleText: bundleText, mount: mount };
  if (root.RqaAgentsBridge && typeof document !== 'undefined') mount(root.RqaAgentsBridge);
})(typeof window !== 'undefined' ? window : globalThis);
/* END CUSTOM_BULKARM_BY_CLAUDE */
